import { noop, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FocusableOption } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
  Output,
  EventEmitter,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PhxRadioButtonDirective, PhxRadioDirective, PhxRadioTileDirective } from './phx-radio-list.directive';
import { PhxRadioListService } from './phx-radio-list.service';

import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PhxRadioListBackground, PhxRadioListSize, PhxRadioTileType, PhxRadioValue } from './phx-radio-list.types';
import { PhxTileComponent } from '../tile/phx-tile.component';
import { PhxTileSize } from '../tile/phx-tile.types';
import { PHX_BACKGROUND, PHX_SIZE } from '../../constants';
import mixinDisabled from '../../mixins/disabled';

const baseRadioItemMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: '[phx-radio],[phx-radio-button],[phx-radio-tile]',
  styleUrls: ['./phx-radio.scss'],
  templateUrl: './phx-radio.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['phxDisabled'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxRadioItemComponent),
      multi: true,
    },
  ],
  host: {
    class: 'phx-radio-list-item',
    '[class.phx-radio]': 'isRadio',
    '[class.phx-radio-button]': 'isRadioButton',
    '[class.phx-radio-tile]': 'isRadioTile',
    '[class.phx-radio-button-xs]': "phxSize === PHX_SIZE.XS",
    '[class.phx-radio-on-summary]': "phxBackground === PHX_BACKGROUND.SUMMARY",
    '[class.phx-radio-on-dark]': "phxBackground === PHX_BACKGROUND.DARK",
    '[class.phx-radio-checked]': 'isChecked',
    '[class.phx-radio-disabled]': 'phxDisabled || isListDisabled',
    '[class.phx-radio-list-disabled]': 'isListDisabled',
  },
})
export class PhxRadioItemComponent extends baseRadioItemMixin implements ControlValueAccessor, OnDestroy, OnInit, FocusableOption {
  @Input() public phxValue: PhxRadioValue = null;

  @Output() public phxRadioItemClicked: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(PhxTileComponent, { static: false }) phxTileComponent: PhxTileComponent;
  @ViewChild('radioIcon', { static: false }) radioIcon: ElementRef;

  public isRadio = !!this._phxRadioDirective;
  public isRadioButton = !!this._phxRadioButtonDirective;
  public isRadioTile = !!this._phxRadioTileDirective;
  public isChecked: boolean = false;
  public isListDisabled = false;
  public onChange: Function = () => {};
  public onTouched: () => void = noop;
  public phxBackground: PhxRadioListBackground = PHX_BACKGROUND.STANDARD;
  public phxSize: PhxRadioListSize = PHX_SIZE.MD;
  public phxIcon: string;
  public phxTileType: PhxRadioTileType = 'default';
  public phxPrefixTemplate: TemplateRef<any>;
  public phxSuffixTemplate: TemplateRef<any>;

  public readonly PHX_SIZE = PHX_SIZE;
  public readonly PHX_BACKGROUND = PHX_BACKGROUND;

  private _destroy$ = new Subject<void>();

  constructor(
    private _cdr: ChangeDetectorRef,
    @Optional() private _phxRadioListService: PhxRadioListService,
    @Optional() private _phxRadioButtonDirective: PhxRadioButtonDirective,
    @Optional() private _phxRadioDirective: PhxRadioDirective,
    @Optional() private _phxRadioTileDirective: PhxRadioTileDirective
  ) {
    super();
  }

  public focus(): void {
    if (this.isRadio) this.radioIcon.nativeElement.focus();
    else this.phxTileComponent.focus();
  }

  public writeValue(value: any): void {
    this.isChecked = value;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public ngOnInit(): void {
    if (this._phxRadioListService) {
      this._phxRadioListService.disabled$.pipe(takeUntil(this._destroy$)).subscribe((disabled) => {
        this.isListDisabled = disabled;
        this._cdr.markForCheck();
      });
      this._phxRadioListService.selected$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
        this.isChecked = this.phxValue === value;
        this._cdr.markForCheck();
      });
      this._phxRadioListService.background$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
        this.phxBackground = value;
        this._cdr.markForCheck();
      });
      this._phxRadioListService.size$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
        this.phxSize = value;
        this._cdr.markForCheck();
      });
    }

    if (this._phxRadioTileDirective) {
      this.phxIcon = this._phxRadioTileDirective.phxIcon;
      this.phxTileType = this._phxRadioTileDirective.phxType;
      this.phxPrefixTemplate = this._phxRadioTileDirective.phxPrefixTemplate;
      this.phxSuffixTemplate = this._phxRadioTileDirective.phxSuffixTemplate;
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public handleKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.SPACE:
      case KeyboardKeys.ENTER:
        event.preventDefault();
        (event.target as HTMLInputElement).click();
        break;
    }
  }

  public onHostClick(event: MouseEvent): void {
    event.preventDefault();
    if (!this.isChecked) {
      if (this._phxRadioListService) {
        this._phxRadioListService.selectInternal(this.phxValue);
      }
      this.onChange(true);
    }
    this.phxRadioItemClicked.emit();
  }

  public get size(): PhxTileSize {
    if (this.isRadioTile) return PHX_SIZE.LG; // For radio-tile
    if (this.isRadioButton) return null; // For radio-button
    return this.phxSize; // For default radio
  }

  public get showPrefix(): boolean {
    return this.isRadio || (this.isRadioTile && (this.phxTileType !== 'default' || !!this.phxPrefixTemplate));
  }

  public get showSuffix(): boolean {
    return !!this.phxSuffixTemplate;
  }
}
